import { useState } from "react";
import { toast } from "sonner";
import { FileDown, FileText, FileSpreadsheet, CheckSquare, Square, Layers } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const SECTIONS = [
    { key: "executive_summary",    label: "Executive Summary" },
    { key: "portfolio_dashboard",  label: "Executive Dashboard KPIs" },
    { key: "business_value",       label: "Business Value Dashboard" },
    { key: "edi_kpis",             label: "Enterprise Delivery Intelligence KPIs" },
    { key: "portfolio_risks",      label: "Portfolio Risks" },
    { key: "commercial_exposure",  label: "Commercial Exposure" },
    { key: "procurement",          label: "Procurement Intelligence" },
    { key: "contract",             label: "Contract Intelligence" },
    { key: "engineering",          label: "Engineering Intelligence" },
    { key: "ai_recommendations",   label: "AI Recommendations" },
];

const FORMATS = [
    { key: "pdf",  label: "PDF",  icon: FileDown },
    { key: "docx", label: "Word", icon: FileText },
    { key: "xlsx", label: "Excel", icon: FileSpreadsheet },
];

function composeMarkdown(selected) {
    const parts = ["# Executive Board Pack", "# SGS Bateman Portfolio · Feb 2026", ""];
    if (selected.executive_summary) {
        parts.push("## Executive Summary");
        parts.push("The SGS Bateman portfolio is trending within tolerance, with cost and schedule performance improving through Enterprise Delivery Intelligence™ reuse. Highlights this cycle include record engineering reuse (47.3%), estimated portfolio savings of $32.76M year-to-date, and a healthy tender pipeline across mining, metals and energy sectors.");
        parts.push("");
    }
    if (selected.portfolio_dashboard) {
        parts.push("## Portfolio Dashboard KPIs");
        parts.push("- Active Projects: 8");
        parts.push("- Portfolio Value: $584.8M under management");
        parts.push("- Portfolio CPI: 0.98");
        parts.push("- Portfolio SPI: 0.95");
        parts.push("- Open Tenders: 5");
        parts.push("");
    }
    if (selected.business_value) {
        parts.push("## Business Value Dashboard");
        parts.push("- Engineering Hours Saved: 218,400 (equivalent to 105 FTEs)");
        parts.push("- Estimated Financial Savings: $32.76M");
        parts.push("- Reduction in Bid Preparation Time: -52.5% (24 d → 11.4 d)");
        parts.push("- Reduction in Data Sheet Preparation: -85% (12 h → 1.8 h)");
        parts.push("- Reduction in Engineering Rework: -66.8%");
        parts.push("- Reduction in Procurement Cycle: -39.3% (112 d → 68 d)");
        parts.push("");
    }
    if (selected.edi_kpis) {
        parts.push("## Enterprise Delivery Intelligence KPIs");
        parts.push("- Knowledge Assets: 2.41M documents");
        parts.push("- Engineering Reuse: 47.3%");
        parts.push("- Reusable Projects: 312");
        parts.push("- Reusable Data Sheets: 18,940");
        parts.push("- Drawings Reused: 42,180");
        parts.push("- Knowledge Growth (12m): +24.8%");
        parts.push("- Engineering Productivity Index: 1.34×");
        parts.push("- Contract Risk Index: 0.28");
        parts.push("- Knowledge Utilisation: 71.5%");
        parts.push("");
    }
    if (selected.portfolio_risks) {
        parts.push("## Portfolio Risks");
        parts.push("- Cape Town Desalination is Critical (SPI 0.79) — escalate to steerco.");
        parts.push("- Nampula Ammonia FEED is At Risk (CPI 0.91) — long-lead reactor package under close watch.");
        parts.push("- Rustenburg Refractory tender pending award — timeline slippage tolerable up to 2 weeks.");
        parts.push("");
    }
    if (selected.commercial_exposure) {
        parts.push("## Commercial Exposure");
        parts.push("- Total contract value under management: $614M");
        parts.push("- Open claims value: $18.2M (6 claims)");
        parts.push("- Variations issued (30 d): 18");
        parts.push("- Contracts expiring within 60 days: 4");
        parts.push("");
    }
    if (selected.procurement) {
        parts.push("## Procurement Intelligence");
        parts.push("- 138 active POs across the portfolio.");
        parts.push("- 24 long-lead items on watch — Ammonia Reactor Package (Nampula) at highest risk.");
        parts.push("- Delivery OTIF: 88.4%.");
        parts.push("- Average supplier score: 4.2 / 5.");
        parts.push("");
    }
    if (selected.contract) {
        parts.push("## Contract Intelligence");
        parts.push("- 42 active contracts.");
        parts.push("- Highest-risk contract: SGSB-1003-CTR-001 (Aveng Grinaker-LTA) — claim quantum exceeds contingency.");
        parts.push("- 8 awards scheduled to close this quarter.");
        parts.push("");
    }
    if (selected.engineering) {
        parts.push("## Engineering Intelligence");
        parts.push("- Under-review deliverables: 418.");
        parts.push("- Overdue drawings: 38 — Piping and Process are the most stressed disciplines.");
        parts.push("- First-pass approval rate: 84%.");
        parts.push("");
    }
    if (selected.ai_recommendations) {
        parts.push("## AI Recommendations");
        parts.push("- Reallocate contingency to Cape Town Desalination to stabilise SPI.");
        parts.push("- Accelerate the Rustenburg refractory tender award before end of Q1.");
        parts.push("- Trigger succession planning for Copper Solvent Extraction — retirement risk is High.");
        parts.push("- Promote the Harmony Kusasalethu Gold CIP baseline to a corporate reusable standard.");
        parts.push("");
    }
    return parts.join("\n");
}

export default function BoardPackGenerator() {
    const [selected, setSelected] = useState(() => Object.fromEntries(SECTIONS.map((s) => [s.key, true])));
    const [busy, setBusy] = useState(null);

    const toggle = (k) => setSelected((s) => ({ ...s, [k]: !s[k] }));

    const generate = async (fmt) => {
        const chosen = SECTIONS.filter((s) => selected[s.key]);
        if (!chosen.length) {
            toast.error("Select at least one section");
            return;
        }
        setBusy(fmt);
        try {
            const content = composeMarkdown(selected);
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title: "Executive Board Pack",
                    content,
                    fmt,
                    confidentiality: "Board",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : `SGSBateman_Board_Pack.${fmt}`;
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success(`${fmt.toUpperCase()} board pack downloaded`);
        } catch {
            toast.error("Board pack generation failed");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="glass rounded-xl p-6" data-testid={EDI.boardPackRoot}>
            <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                <Layers className="h-4 w-4" strokeWidth={1.5} />
                <span className="kicker text-[10px]">Executive board pack composer</span>
            </div>
            <h3 className="mt-1 font-display font-black text-white text-lg tracking-tight">
                Compose &amp; export a board-ready pack
            </h3>
            <p className="text-slate-400 text-sm font-body mt-1">
                Select the sections you want in the board pack. Every export uses the SGS Bateman
                cover page, header, footer, page numbering and "Confidential · Board" watermark.
            </p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                {SECTIONS.map((s) => {
                    const on = !!selected[s.key];
                    const Icon = on ? CheckSquare : Square;
                    return (
                        <button
                            key={s.key}
                            data-testid={EDI.boardPackSection(s.key)}
                            onClick={() => toggle(s.key)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-left text-sm font-body transition-colors duration-200 ${
                                on
                                    ? "border-[color:var(--sgsb-gold)]/50 bg-[color:var(--sgsb-gold)]/10 text-white"
                                    : "border-slate-800 bg-slate-950/50 text-slate-300 hover:border-slate-700"
                            }`}
                        >
                            <Icon className="h-4 w-4" style={{ color: on ? "#D4AF37" : "#94A3B8" }} strokeWidth={1.5} />
                            {s.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="kicker text-slate-400 text-[10px] mr-2">Export</span>
                {FORMATS.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        data-testid={EDI.boardPackExport(key)}
                        disabled={busy === key}
                        onClick={() => generate(key)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-slate-950/60 px-3 py-1.5 text-xs font-display font-bold text-[var(--sgsb-gold)] hover:bg-[var(--sgsb-gold)] hover:text-slate-950 disabled:opacity-60 transition-colors duration-200"
                    >
                        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                        {busy === key ? "Preparing…" : label}
                    </button>
                ))}
            </div>
        </div>
    );
}
